import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Lock, Eye, EyeOff, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../../lib/walletApi';

export default function ForcePasswordReset() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user, redirectTo } = location.state || {};

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!token || !user) {
    navigate('/login', { replace: true });
    return null;
  }

  const passwordValid = newPassword.length >= 8;
  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit = passwordValid && passwordsMatch && newPassword !== 'admin123';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Temporarily set the token so the API call is authenticated
    localStorage.setItem('wallet_token', token);

    setLoading(true);
    try {
      await authAPI.forceChangePassword(newPassword);
      toast.success('Password changed successfully');

      // Now set the full auth context and navigate to dashboard
      localStorage.setItem('wallet_user', JSON.stringify(user));
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (e) {
      localStorage.removeItem('wallet_token');
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="force-password-reset">
      <Card className="w-full max-w-md border-gray-200 shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
            <ShieldCheck className="w-6 h-6 text-amber-500" />
          </div>
          <CardTitle className="text-lg font-bold text-gray-900">Set a New Password</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Your account requires a password update before you can continue.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">New Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pl-9 pr-10 h-10"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {newPassword && !passwordValid && (
                <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
              )}
              {newPassword === 'admin123' && (
                <p className="text-xs text-red-500 mt-1">Please choose a different password</p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pl-9 h-10"
                  data-testid="confirm-password-input"
                />
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-gray-900 hover:bg-gray-800 text-white h-10"
              disabled={!canSubmit || loading}
              data-testid="change-password-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Change Password & Continue
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
